/**
 * IS A NEWER RELEASE LIVE? (rc.9 item 2; Backend's security R-4, Web Director.)
 *
 * A page that stays open across a deploy keeps running the OLD build. It must
 * not send a booking, a guest code request or an account creation from that
 * build: Backend's contracts move with releases. So before those sends, and
 * when the person comes back to the tab, Book compares the commit it was
 * built from (`__LURA_BUILD_COMMIT__`) with the deployed `/build-info.json`
 * (served no-cache). A difference means a newer release is live: nothing is
 * sent, and the page offers a reload, which keeps only what the URL carries.
 *
 * - FAILS OPEN. A build without a known commit, a network error, a timeout
 *   (1.5 s), a non-200, or an answer without a commit all count as current:
 *   a flaky fetch never blocks a booking.
 * - BOUNDED. No polling: at most one check per return to the tab and one per
 *   send, and a "current" answer is reused for 60 s, so most sends add 0 ms.
 *   Concurrent asks share one request.
 * - NEVER MID-SEND. While a send is in flight or its outcome is uncertain,
 *   a return to the tab checks nothing and the notice stays hidden: a reload
 *   then would lose the attempt (and its Check).
 * - STICKY. Once a newer release is seen, this document stays stale.
 */

const OWN_COMMIT: string = __LURA_BUILD_COMMIT__;
const FULL_SHA = /^[0-9a-f]{40}$/;
export const BUILD_INFO_PATH = "/build-info.json";
const TIMEOUT_MS = 1500;
const REUSE_MS = 60_000;

let stale = false;
let currentAt = -Infinity;
// When build-info last CONFIRMED this build (a read that named our commit).
// `currentAt` also moves on an unreadable read (the send check fails open);
// a failed chunk must not reuse that as "current" (review of 45e8dfe1).
let confirmedAt = -Infinity;
let pending: Promise<boolean> | null = null;
let busy = 0;
let version = 0;
const listeners = new Set<() => void>();

function changed(): void {
  version += 1;
  for (const listener of listeners) listener();
}

/** The deployed commit, or null when it cannot be read (fails open). */
async function deployedCommit(fetchImpl: typeof fetch): Promise<string | null> {
  // The timer both aborts the read and, as a backstop, answers null itself:
  // a fetch that ignored the signal must not hold every send on "sending"
  // (review of d8f834c, P3).
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve(null);
    }, TIMEOUT_MS);
  });
  try {
    return await Promise.race([read(fetchImpl, controller.signal), timedOut]);
  } finally {
    clearTimeout(timer);
  }
}

async function read(fetchImpl: typeof fetch, signal: AbortSignal): Promise<string | null> {
  try {
    const response = await fetchImpl(BUILD_INFO_PATH, { cache: "no-store", signal });
    if (!response.ok) return null;
    const info: unknown = await response.json();
    const web = info && typeof info === "object" ? (info as { web?: unknown }).web : null;
    const commit = web && typeof web === "object" ? (web as { commit?: unknown }).commit : null;
    return typeof commit === "string" && FULL_SHA.test(commit) ? commit : null;
  } catch {
    return null;
  }
}

/**
 * True when this build may send: no newer release is known to be live.
 * Resolves at once from a recent "current" answer.
 */
export function isCurrentBuild(fetchImpl: typeof fetch = fetch, now: () => number = Date.now): Promise<boolean> {
  if (stale) return Promise.resolve(false);
  if (!FULL_SHA.test(OWN_COMMIT) || now() - currentAt < REUSE_MS) return Promise.resolve(true);
  if (pending) return pending;
  pending = deployedCommit(fetchImpl).then((deployed) => {
    pending = null;
    if (deployed !== null && deployed !== OWN_COMMIT) {
      stale = true;
      changed();
      return false;
    }
    currentAt = now();
    if (deployed === OWN_COMMIT) confirmedAt = currentAt;
    return true;
  });
  return pending;
}

/** What build-info says about a failed chunk. See `chunkFailureVerdict`. */
export type ChunkFailureVerdict = "newer" | "current" | "unknown";

/**
 * A CHUNK OF THIS BUILD FAILED TO LOAD: IS A NEWER RELEASE LIVE? (rc.12 (a),
 * the Web Director's amendment of rc.9's rule, for every chunk: the
 * appointment page, the step chunks, the Spanish catalogue.)
 *
 * A missing hashed chunk is what a newer release looks like (Hosting answers
 * it with the HTML shell), but so is a network failure, and a prefetch that
 * failed once fails every later import of that URL in this document (the
 * module map remembers it). So the failure ASKS:
 *   - "newer": build-info names another commit. The document is stale, and
 *     the page's one release notice offers the reload.
 *   - "current": build-info names this build. Not a new version: the caller
 *     shows the honest generic failure.
 *   - "unknown": no answer (offline, unreachable, unreadable, a build without
 *     a known commit). Also NOT a new version.
 *
 * THE FAIL DIRECTION IS THE OPPOSITE OF `isCurrentBuild`'s. There, unknown
 * counts as current so a flaky read never blocks a booking; here, unknown
 * counts as "not newer" so the page never claims a version it has not seen.
 * The two must not be swapped: tests pin both (staleBuild.test.ts).
 */
export async function chunkFailureVerdict(fetchImpl: typeof fetch = fetch, now: () => number = Date.now): Promise<ChunkFailureVerdict> {
  if (stale) return "newer";
  if (!FULL_SHA.test(OWN_COMMIT)) return "unknown";
  if (now() - confirmedAt < REUSE_MS) return "current";
  const deployed = await deployedCommit(fetchImpl);
  if (deployed === null) return "unknown";
  if (deployed === OWN_COMMIT) {
    currentAt = confirmedAt = now();
    return "current";
  }
  if (!stale) {
    stale = true;
    changed();
  }
  return "newer";
}

/** The person came back to the tab: check, unless a send is in flight. */
export function checkOnReturn(fetchImpl: typeof fetch = fetch, now: () => number = Date.now): void {
  if (busy > 0 || stale) return;
  void isCurrentBuild(fetchImpl, now);
}

/** A send started (true) or settled (false). Uncertain outcomes stay busy. */
export function markBusy(on: boolean): void {
  busy = Math.max(0, busy + (on ? 1 : -1));
  changed();
}

/** Whether the reload notice should show now: stale, and nothing in flight. */
export function showStaleNotice(): boolean {
  return stale && busy === 0;
}

export function subscribeStaleBuild(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function staleBuildVersion(): number {
  return version;
}

/** Tests only: a fresh document. */
export function __resetStaleBuildForTests(): void {
  stale = false;
  currentAt = -Infinity;
  confirmedAt = -Infinity;
  pending = null;
  busy = 0;
  changed();
}
